import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { SegmentControl } from '@blade/ui-kit'
import { Drawer } from '@root/shared/ui-kit'

import { ReferralLeaderboardTable } from './referral-leaderboard-table'
import { ReferralRankCard } from './referral-rank-card'
import { TradingLeaderboardTable } from './trading-leaderboard-table'
import { TradingRankCard } from './trading-rank-card'

import type { DrawerProps } from '@root/shared/ui-kit'

export type LeaderboardDrawerTab = 'referral' | 'trading'

type Props = Pick<DrawerProps, 'onOpenChange' | 'open'> & {
  initialTab: LeaderboardDrawerTab | undefined
  renderFooter?: (tab: LeaderboardDrawerTab) => React.ReactNode
}

export const LeaderboardDrawer: React.FC<Props> = ({
  open,
  onOpenChange,
  initialTab,
  renderFooter,
}) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState<LeaderboardDrawerTab>(initialTab ?? 'referral')

  useEffect(() => {
    if (initialTab) {
      setTab(initialTab)
    }
  }, [initialTab])

  const footer = renderFooter?.(tab)

  return (
    <>
      <Root
        open={open}
        onOpenChange={onOpenChange}
        title={t('profile.leaderboard')}
      >
        <Content>
          <div
            css={css`
              padding-inline: 12px;
              margin-bottom: 22px;
            `}
          >
            {tab === 'referral' ? <ReferralRankCard /> : <TradingRankCard />}
          </div>
          <TabsContainer>
            <SegmentControl size="h30" value={tab} onChange={setTab}>
              <SegmentControl.Item
                value={'trading' satisfies LeaderboardDrawerTab}
              >
                {t('trading')}
              </SegmentControl.Item>
              <SegmentControl.Item
                value={'referral' satisfies LeaderboardDrawerTab}
              >
                {t('referral')}
              </SegmentControl.Item>
            </SegmentControl>
          </TabsContainer>
          {tab === 'referral' ? (
            <ReferralLeaderboardTable />
          ) : (
            <TradingLeaderboardTable />
          )}

          {footer ? <Footer>{footer}</Footer> : undefined}
        </Content>
      </Root>
    </>
  )
}

const Root = styled(Drawer)`
  height: var(--max-drawer-height);
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  padding-block: 16px 0;
  height: 100%;
  position: relative;
`

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 22px;
`

const Footer = styled.div`
  padding: 12px;
`
